import React from 'react';
import { connect } from 'react-redux';
import { VaPagination } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import PropTypes from 'prop-types';

import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import backendServices from '@department-of-veterans-affairs/platform-user/profile/backendServices';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';
import { datadogRum } from '@datadog/browser-rum';
import {
  appealsAvailability,
  appealTypes,
  claimsAvailability,
  groupClaimsByDocsNeeded,
  isClosed,
  sortByLastUpdated,
  withClaimStatusMetaIfEnabled,
} from '~/platform/shared/claims-appeals';
import { ClaimOrAppealCard } from '~/platform/shared/claims-appeals/components/ClaimOrAppealCard';
import withRouter from '../utils/withRouter';
import manifest from '../manifest.json';

import {
  getAppealsV2 as getAppealsV2Action,
  getClaims as getClaimsAction,
} from '../actions';

import AppealListItem from '../components/appeals-v2/AppealListItem';
import ClaimCardLoadingSkeleton from '../components/ClaimCard/ClaimCardLoadingSkeleton';
import NeedHelp from '../components/NeedHelp';
import ServiceUnavailableAlert from '../components/ServiceUnavailableAlert';
import ClaimsBreadcrumbs from '../components/ClaimsBreadcrumbs';
import ClaimsListItem from '../components/ClaimsListItem';
import FeaturesWarning from '../components/FeaturesWarning';
import NoClaims from '../components/NoClaims';
import TravelClaimsSection from '../components/TravelClaimsSection';
import VreBenefitsSection from '../components/VreBenefitsSection';

import { CST_HOME_H1, FILTER_VALUES, ITEMS_PER_PAGE } from '../constants';

import { getBackendServices, hasClaimsAccess } from '../selectors';

import { getPageRange, getVisibleRows } from '../utils/appeals-v2-helpers';
import { setPageFocus } from '../utils/page';
import { setDocumentTitle } from '../utils/helpers';
import ClaimsFilter from '../components/ClaimsFilter';
import ClaimLetterSection from '../components/claim-letters/ClaimLetterSection';
import IntentToFileSection from '../components/IntentToFileSection';

class YourClaimsPageV2 extends React.Component {
  constructor(props) {
    super(props);
    this.changePage = this.changePage.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleCardLinkClick = this.handleCardLinkClick.bind(this);

    this.state = {
      page: YourClaimsPageV2.getPageFromURL(props),
      claimsFilter: sessionStorage.getItem('claimsFilter') || FILTER_VALUES.ALL,
    };
  }

  componentDidUpdate(prevProps) {
    const wasLoading = prevProps.claimsLoading || prevProps.appealsLoading;
    const allLoaded = !(this.props.claimsLoading || this.props.appealsLoading);

    if (wasLoading && allLoaded) {
      datadogRum.addAction('claims-list-loaded', {
        openCount: this.props.openCount,
        closedCount: this.props.closedCount,
        totalCount: this.props.openCount + this.props.closedCount,
      });
    }
  }

  componentDidMount() {
    setDocumentTitle(CST_HOME_H1);

    const { canAccessAppeals, canAccessClaims, getAppealsV2, getClaims } =
      this.props;

    // Only call if the current user has access to Lighthouse claims
    if (canAccessClaims) {
      getClaims();
    }

    if (canAccessAppeals) {
      getAppealsV2();
    }

    focusElement('h1');
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const newPage = YourClaimsPageV2.getPageFromURL(nextProps);

    if (newPage !== prevState.page) {
      return {
        page: newPage,
      };
    }
    return null;
  }

  static getPageFromURL(props) {
    const queryParams = new URLSearchParams(props.location.search);
    return parseInt(queryParams.get('page'), 10) || 1;
  }

  changePage(event) {
    const newURL = `${this.props.location.pathname}?page=${event.detail.page}`;
    this.props.navigate(newURL);
    this.setState({ page: event.detail.page });
    // Move focus to pagination info for screenreaders
    setPageFocus('#pagination-info');
  }

  handleFilterChange(filter) {
    sessionStorage.setItem('claimsFilter', filter);
    // Strip ?page= via history.replace — avoids adding Back-button entries and
    // inflating GA/Datadog pageviews for this route. Drops the whole query string;
    // will clobber other params if any get added later.
    const hasPageParam = new URLSearchParams(this.props.location.search).has(
      'page',
    );
    if (hasPageParam) {
      this.props.navigate(this.props.location.pathname, { replace: true });
    }
    this.setState({ claimsFilter: filter, page: 1 });
  }

  handleCardLinkClick(e, href) {
    e.preventDefault();
    this.props.navigate(href.replace(manifest.rootUrl, ''));
  }

  getFilteredList() {
    const { list } = this.props;
    const { claimsFilter } = this.state;

    if (claimsFilter === FILTER_VALUES.ALL) return list;

    return list.filter(item =>
      claimsFilter === FILTER_VALUES.CLOSED ? isClosed(item) : !isClosed(item),
    );
  }

  renderListItem(claim) {
    if (this.props.cstClaimOrAppealCardEnabled) {
      const { cstChampvaCustomContentEnabled } = this.props;
      const isAppeal = appealTypes.includes(claim.type);
      const item = isAppeal
        ? claim
        : withClaimStatusMetaIfEnabled(claim, cstChampvaCustomContentEnabled);

      return (
        <ClaimOrAppealCard
          key={item.id}
          item={item}
          onLinkClick={this.handleCardLinkClick}
        />
      );
    }

    const sanitizedClaim = withClaimStatusMetaIfEnabled(
      claim,
      this.props.cstChampvaCustomContentEnabled,
    );

    if (appealTypes.includes(claim.type)) {
      const { fullName } = this.props;
      return <AppealListItem key={claim.id} appeal={claim} name={fullName} />;
    }

    return <ClaimsListItem key={sanitizedClaim.id} claim={sanitizedClaim} />;
  }

  renderErrorMessages() {
    const {
      claimsLoading,
      appealsLoading,
      appealsAvailable,
      canAccessAppeals,
      canAccessClaims,
      claimsAvailable,
      // claimsAuthorized
    } = this.props;

    if (claimsLoading || appealsLoading) {
      return null;
    }

    // Determine which services are unavailable
    // Service keys must match SERVICE_REGISTRY in constants.js
    const unavailableServices = [];

    if (canAccessClaims && claimsAvailable !== claimsAvailability.AVAILABLE) {
      unavailableServices.push('claims');
    }

    if (
      canAccessAppeals &&
      appealsAvailable !== appealsAvailability.AVAILABLE
    ) {
      unavailableServices.push('appeals');
    }

    if (unavailableServices.length > 0) {
      return (
        <ServiceUnavailableAlert
          services={unavailableServices}
          headerLevel={3}
        />
      );
    }

    return null;
  }

  render() {
    const { appealsLoading, claimsLoading } = this.props;

    const filteredList = this.getFilteredList();

    let content;
    let pageInfo;
    const allRequestsLoaded = !claimsLoading && !appealsLoading;
    const emptyFilteredList = !(filteredList && filteredList.length);
    const { claimsFilter } = this.state;

    // Wait for all requests to complete before rendering results
    // This prevents multiple re-renders as each request completes
    if (!allRequestsLoaded) {
      content = <ClaimCardLoadingSkeleton />;
    } else if (!emptyFilteredList) {
      const listLen = filteredList.length;
      const numPages = Math.ceil(listLen / ITEMS_PER_PAGE);
      const shouldPaginate = numPages > 1;

      const recordText = listLen === 1 ? 'record' : 'records';
      const filterLabel =
        claimsFilter === FILTER_VALUES.ALL
          ? recordText
          : `${claimsFilter} ${recordText}`;

      const pageItems = getVisibleRows(filteredList, this.state.page);

      const range = getPageRange(this.state.page, listLen);
      const { end, start } = range;

      pageInfo = (
        <p id="pagination-info">
          {`Showing ${start}-${end} of ${listLen} ${filterLabel}`}
        </p>
      );

      content = (
        <>
          {pageInfo}
          <div className="claim-list">
            {pageItems.map(claim => this.renderListItem(claim))}
            <ClaimCardLoadingSkeleton isLoading={false} />
            {shouldPaginate && (
              <VaPagination
                page={this.state.page}
                pages={numPages}
                onPageSelect={this.changePage}
              />
            )}
          </div>
        </>
      );
    } else {
      const noClaimsLabel =
        claimsFilter === FILTER_VALUES.ALL
          ? 'records'
          : `${claimsFilter} records`;
      content = <NoClaims recordType={noClaimsLabel} />;
    }

    return (
      <article className="row">
        <div className="usa-width-two-thirds medium-8 columns">
          <div className="breadcrumbs-loading-container">
            <ClaimsBreadcrumbs />
          </div>
          <h1 className="claims-container-title">{CST_HOME_H1}</h1>
          <div className="on-this-page-loading-container">
            <va-on-this-page />
          </div>
          <h2 id="your-claims-or-appeals" className="vads-u-margin-top--2p5">
            Your claims, decision reviews, or appeals
          </h2>
          <div>{this.renderErrorMessages()}</div>
          <div className="claims-filter-container vads-u-margin-y--2">
            <ClaimsFilter
              selected={this.state.claimsFilter}
              onFilterChange={this.handleFilterChange}
            />
          </div>
          {content}
          <IntentToFileSection />
          <ClaimLetterSection />
          <h2 id="if-you-cant-find-your-appeal">
            If you can't find your claim, decision review, or appeal
          </h2>
          <h3>We might still be processing it</h3>
          <p>
            If you recently submitted a claim or requested a Higher-Level Review
            or Board Appeal, we might still be processing it. You can check back
            for updates.
          </p>
          <h3>We may have combined your claims</h3>
          <p>
            If you submit a new claim while we're reviewing an existing one, we
            may combine your claims. If we combine your claims, we'll add any
            new information to your existing claim. This means you may not find
            your new claim in your list. You don't need to do anything.
          </p>
          <TravelClaimsSection />
          <VreBenefitsSection />
          <FeaturesWarning />
          <NeedHelp />
        </div>
      </article>
    );
  }
}

YourClaimsPageV2.propTypes = {
  appealsAvailable: PropTypes.string,
  appealsLoading: PropTypes.bool,
  canAccessAppeals: PropTypes.bool,
  canAccessClaims: PropTypes.bool,
  claimsAvailable: PropTypes.string,
  claimsLoading: PropTypes.bool,
  closedCount: PropTypes.number,
  cstChampvaCustomContentEnabled: PropTypes.bool,
  cstClaimOrAppealCardEnabled: PropTypes.bool,
  fullName: PropTypes.shape({}),
  getAppealsV2: PropTypes.func,
  getClaims: PropTypes.func,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      attributes: PropTypes.shape({}),
    }),
  ),
  location: PropTypes.object,
  navigate: PropTypes.func,
  openCount: PropTypes.number,
};

function mapStateToProps(state) {
  const claimsState = state.disability.status;
  const claimsV2Root = claimsState.claimsV2; // this is where all the meat is for v2

  const services = getBackendServices(state);
  const canAccessAppeals = services.includes(backendServices.APPEALS_STATUS);

  const cstChampvaCustomContentEnabled =
    toggleValues(state)[FEATURE_FLAG_NAMES.cstChampvaCustomContent];

  const cstClaimOrAppealCardEnabled =
    toggleValues(state)[FEATURE_FLAG_NAMES.cstClaimOrAppealCard];

  // TO-DO: Implement with reselect to save cycles
  const closedClaims = [...claimsV2Root.appeals, ...claimsV2Root.claims]
    .filter(isClosed)
    .sort(sortByLastUpdated);

  const inProgressClaims = [...claimsV2Root.appeals, ...claimsV2Root.claims]
    .filter(item => !isClosed(item))
    .sort(sortByLastUpdated);

  const sortedList = [...inProgressClaims, ...closedClaims];

  return {
    appealsAvailable: claimsV2Root.v2Availability,
    appealsLoading: claimsV2Root.appealsLoading,
    canAccessAppeals,
    canAccessClaims: hasClaimsAccess(state),
    claimsAvailable: claimsV2Root.claimsAvailability,
    claimsLoading: claimsV2Root.claimsLoading,
    closedCount: closedClaims.length,
    cstChampvaCustomContentEnabled,
    cstClaimOrAppealCardEnabled,
    fullName: state.user.profile.userFullName,
    list: groupClaimsByDocsNeeded(sortedList),
    openCount: inProgressClaims.length,
  };
}

const mapDispatchToProps = {
  getAppealsV2: getAppealsV2Action,
  getClaims: getClaimsAction,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(YourClaimsPageV2),
);

export { YourClaimsPageV2 };
